import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { PickerController, Platform, ToastController } from '@ionic/angular';
import { KilnService } from 'src/app/services/kiln.service';
import { ToggleService } from 'src/app/services/toggle.service';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';

// The kiln API only appends the S3 firing archive when the requested range starts before its
// 02/01/2025 cutoff, so older history is reached by walking the range backwards a window at a time
// until it predates the archive itself.
const ARCHIVE_WINDOW_MONTHS = 6;
const ARCHIVE_START_DATE = '01/01/2016';

@Component({
  selector: 'app-firing-history',
  templateUrl: './firing-history.component.html',
  styleUrls: ['./firing-history.component.scss'],
})
export class FiringHistoryComponent implements OnInit {
  @Input() mac;
  @Input() serial;
  @Input() kilnId;
  @Input() t_scale;
  @Input() viewingKilnlinkFreeKilns;
  public error = false;
  public retrieving = false;
  public firing_history = [];
  public details = undefined;
  public subtab = 'program';
  public selected_firing = undefined;
  public editingNote = false;
  public submittingNote;
  public program_steps = [];
  public hasMore = false;
  public canLoadMore = false;
  private page = 0;
  private loadingArchive = false;
  private archiveExhausted = false;
  public retrieving_more = false;
  public requestingProgramCsv = false;
  public small_screen;
  public firingHistoryPickers = [];
  public table_height;
  public graphDimensions = {height: 380, width: 950};
  private earliestDate: string = null;
  private currentStartDate: string = null;
  private currentEndDate: string = null;

  public pickerButtons = [
    {
      text: 'Cancel',
      role: 'cancel',
    },
    {
      text: 'Confirm',
      handler: (selection) => {
        if(selection.historical_firings.value && selection.historical_firings.value === 'load_more') {
          this.getMoreFiringHistory();
        } else if(selection.historical_firings.value) {
          this.selectFiring(selection.historical_firings.value.id)
        }        
      },
    },
  ];

  public firingNotesForm: UntypedFormGroup;

  constructor(
    public kilnService: KilnService,
    public toastCtrl: ToastController,
    private platform: Platform,
    private pickerController: PickerController,
    private toggleService: ToggleService,
    private translate: TranslateService
  ) {
    this.error = false;
    if(!this.viewingKilnlinkFreeKilns) {
      this.getFiringHistory()
    }
  }

  ngOnInit() {
    this.program_steps = [];
    this.submittingNote = false;
    this.editingNote = false;

    if(!this.viewingKilnlinkFreeKilns) {
      this.getFiringHistory();
    }
    this.subtab = 'program';

    let width = this.platform.width();
    let is_device = this.platform ? (this.platform.is('mobile') || this.platform.is('tablet') || this.platform.is('phablet') || this.platform.is('mobileweb') || this.platform.is('desktop')) : false;
    this.small_screen = width < 875 && is_device;

    this.table_height = this.platform.height() * 0.75;
    this.graphDimensions = {width: this.platform.width() > 800 ? 900 : this.platform.width(), height: this.platform.height() < 400 ? this.platform.height() : 380}
  }

  ngOnChanges() {
    this.program_steps = [];
    this.details = undefined;
    this.selected_firing = undefined;

    if(!this.viewingKilnlinkFreeKilns) {
      this.getFiringHistory();
    }

    let width = this.platform.width();
    let is_device = this.platform ? (this.platform.is('mobile') || this.platform.is('tablet') || this.platform.is('phablet') || this.platform.is('mobileweb') || this.platform.is('desktop')) : false;
    this.small_screen = width < 875 && is_device;
  }

  async showPicker() {
    let picker = await this.pickerController.create({
      columns: [
      {
        name: 'historical_firings',
        options: this.firingHistoryPickers
      }],
      buttons: this.pickerButtons
    });

    await picker.present();
  }

  unsubscribe() {
    this.program_steps = [];
  }
  ngOnDestroy() { this.unsubscribe(); }
  ngOnLeave() { this.unsubscribe(); }

  getNotesSuccess(data) {
    // console.log(`getNotesSuccess: ${JSON.stringify(data)} ${typeof data}`)
    let note = (data && data.length >= 1) ? data[0] : data;
    this.createForm(note);
  }

  createForm(data) {
    if(this.selected_firing && data) {
      // console.log(`creating edit form: ${JSON.stringify(data)}, ${typeof data} ${data.load_density || data.load_description || data.comments}`)
      this.editingNote = data.load_density || data.load_description || data.comments;
      let load_density = new UntypedFormControl(data.load_density || '', [Validators.required]);
      let load_description = new UntypedFormControl(data.load_description || '', [Validators.required]);
      let comments = new UntypedFormControl(data.comments || '', [Validators.required]);

      this.firingNotesForm = new UntypedFormGroup({
        load_density: load_density,
        load_description: load_description,
        comments: comments
      });
    } else {
      let load_density = new UntypedFormControl('', [Validators.required]);
      let load_description = new UntypedFormControl('', [Validators.required]);
      let comments = new UntypedFormControl('', [Validators.required]);
      this.firingNotesForm = new UntypedFormGroup({
        load_density: load_density,
        load_description: load_description,
        comments: comments
      });
    }
  }

  getFiringHistory() {
    if(this.mac && this.serial && !this.retrieving) {
      this.error = false;
      this.retrieving = true;
      // Reset for new initial load
      this.earliestDate = null;
      this.page = 0;
      this.loadingArchive = false;
      this.archiveExhausted = false;
      this.canLoadMore = false;

      // Initial load: today (date A) to today - 1 month
      this.currentEndDate = moment().format('MM/DD/YYYY');
      this.currentStartDate = moment().subtract(1, 'month').format('MM/DD/YYYY');

      this.requestFiringHistory();
    }
  }

  getMoreFiringHistory() {
    if(!this.mac || !this.serial || this.retrieving || this.retrieving_more) {
      return;
    }

    if(!this.loadingArchive && this.hasMore) {
      // The API pages recent firings without applying the date range to them, so only the page moves.
      this.retrieving_more = true;
      this.page = this.page + 1;
      this.requestFiringHistory();
    } else if(!this.archiveExhausted) {
      // Recent firings are exhausted, so walk backwards in 6 month windows ending at the oldest
      // firing loaded so far. Hold the page: the API only appends the S3 archive while the
      // requested page reports no more recent firings.
      this.retrieving_more = true;
      this.loadingArchive = true;

      const endDate = this.nextWindowEndDate();
      const startDate = endDate.clone().subtract(ARCHIVE_WINDOW_MONTHS, 'months');
      this.currentEndDate = endDate.format('MM/DD/YYYY');
      this.currentStartDate = startDate.format('MM/DD/YYYY');
      // Nothing predates the archive, so this is the last window worth asking for.
      this.archiveExhausted = !startDate.isAfter(moment(ARCHIVE_START_DATE, 'MM/DD/YYYY'));

      this.requestFiringHistory();
    } else {
      this.canLoadMore = false;
    }
  }

  /**
   * Windows tile backwards from the oldest firing loaded. The API caps each archive window, so keep
   * ending at that firing while it reports more; once a window adds nothing, fall back to the
   * previous window's start so the range keeps moving instead of repeating.
   */
  private nextWindowEndDate() {
    const earliest = this.earliestDate ? moment(this.earliestDate, 'MM/DD/YYYY') : null;
    if(this.hasMore && earliest && earliest.isValid()) {
      return earliest;
    }

    const candidates = [this.earliestDate, this.currentStartDate]
      .filter(d => !!d)
      .map(d => moment(d, 'MM/DD/YYYY'))
      .filter(d => d.isValid());

    return candidates.length > 0 ? moment.min(candidates) : moment();
  }

  private requestFiringHistory() {
    this.kilnService.getFiringHistory(this.mac, this.serial, this.page, this.currentStartDate, this.currentEndDate)
        .then((response) => { this.getFiringHistorySuccess(response) })
        .catch((error) => { this.getFiringHistoryError(error) })
  }

  /** Track the oldest firing loaded so far; it becomes the end of the next archive range. */
  private updateEarliestDate(firings: any[]) {
    const dates = (firings || [])
      .map((f: any) => f.started ? moment.utc(f.started) : null)
      .filter((d: any) => d && d.isValid());

    if(dates.length === 0) {
      return;
    }

    const earliestDateStr = moment.min(dates).format('MM/DD/YYYY');
    if(!this.earliestDate || moment(earliestDateStr, 'MM/DD/YYYY').isBefore(moment(this.earliestDate, 'MM/DD/YYYY'))) {
      this.earliestDate = earliestDateStr;
    }
  }

  getFiringHistorySuccess(data) {
    let firings = data.firings || [];
    this.hasMore = data.has_more;
    let unique_firing_ids = [];
    let reducedFirings = firings.reduce((accumulator, history) => {
      if(!unique_firing_ids.includes(history.id)) {
        unique_firing_ids.push(history.id);
        accumulator.push(history);
      }

      return accumulator;
    }, []);

    this.updateEarliestDate(reducedFirings);

    if(this.retrieving_more) {
      // Concatenate and remove duplicates based on firing ID
      const existingIds = new Set(this.firing_history.map(f => f.id));
      const newFirings = reducedFirings.filter(f => !existingIds.has(f.id));
      this.firing_history = this.firing_history.concat(newFirings);
      if(this.loadingArchive && newFirings.length === 0) {
        // This window added nothing, so move on to the next one instead of asking for it again.
        this.hasMore = false;
      }
      this.retrieving_more = false;
    } else {
      this.firing_history = reducedFirings;
    }

    // Once recent firings run out there is still the archive to walk back through.
    this.canLoadMore = (!this.loadingArchive && this.hasMore) || !this.archiveExhausted;
    
    // Sort firing history by date in descending order (newest first)
    this.firing_history.sort((firing1, firing2) => {
      if (firing1.started && firing2.started) {
        const date1 = moment(firing1.started);
        const date2 = moment(firing2.started);
        if (date1.isAfter(date2)) return -1;
        if (date1.isBefore(date2)) return 1;
      }
      return 0;
    });

    if(this.small_screen) {
      this.firingHistoryPickers = this.firing_history.map((history) => {
        return {
          text: `${moment(history.started).format('M/DD/YY, h:mm a')} - ${history.name}`,
          value: history
        }
      })

      if(this.canLoadMore && !this.retrieving_more) {
        this.firingHistoryPickers.push({
          text: "Load More",
          value: 'load_more'
        })
      }
    }

    if(this.firing_history && this.firing_history.length > 0) {
      this.selectFiring(this.selected_firing || this.firing_history[0].id);
    } else {
      this.retrieving = false;
      this.details = undefined;
      this.selected_firing = undefined;
    }

    // Only set retrieving to false if this was the initial load (not retrieving_more)
    if(!this.retrieving_more) {
      this.retrieving = false;
    }
    this.error = false;
  }

  getFiringHistoryError(error) {
    console.log(error);
    this.error = true;
    this.retrieving = false;
  }

  getFiringDetailsError(error) {
    if(this.subtab === 'firing-data') {
      this.error = true;
    }
  }

  selectFiring(firing_id) {    
    if(firing_id !== this.selected_firing) {
      this.retrieving = true;
      this.details = undefined;
      this.selected_firing = firing_id;
      this.kilnService.getFiringDetails(firing_id, this.t_scale, true)
          .then((response) => {this.getDetails(response); })
          .catch((error) => { this.getFiringHistoryError(error) })
      this.kilnService.getFiringNotes(firing_id)
          .then((response) => { this.getNotesSuccess(response) })
          .catch((error) => { this.getFiringHistoryError(error) })
    }
  }

  getFiringStatuses(subtab) {
    this.subtab = subtab;
    this.retrieving = true;
    this.kilnService.getPaginatedStatuses(this.selected_firing, this.t_scale)
        .then((response) => {this.updateStatusInformation(response); })
        .catch((error) => { this.getFiringDetailsError(error); })
  }

  getDetails(data) {
    var dateObject = new Date();

    this.details = data;
    this.updateStatusInformation(data);

    if(data && data.statuses && data.statuses.length > 0) {
      if(this.hasEnded(this.details)) {
        this.program_steps[this.program_steps.length - 1].end_time = this.details.updatedAt;
      }
    } else {
      this.kilnService.getPaginatedStatuses(this.selected_firing, this.t_scale)
          .then((response) => { this.updateStatusInformation(response); })
          .catch((error) => { this.getFiringDetailsError(error); })
    }

    this.retrieving = false;
    this.error = false;
  }

  updateStatusInformation(data) {
    if(data && data.statuses && data.statuses.length > 0) {
      this.program_steps = Object.values(data.statuses.reduce((accumulator, stat, current_index) => {
        if(!accumulator.hasOwnProperty(stat.step)) {
          let last_status_for_previous_segment = (current_index > 0 && stat.createdAt) ? data.statuses[current_index - 1] : undefined;
          if(last_status_for_previous_segment) {
            accumulator[last_status_for_previous_segment.step].end_time = last_status_for_previous_segment.createdAt;
          }

          accumulator[stat.step] = {
            name: stat.step,
            start_time: stat.createdAt,
            end_time: '---'
          }
        }

        return accumulator;
      }, {}));
    }
  }

  async showMessage(message, error) {
    let toast = await this.toastCtrl.create({
     message: message,
     duration: 3000,
     position: 'middle',
     cssClass: error ? 'error' : 'success'
    });

    await toast.present();
  }

  submitFiringNote(formValue) {
    if(!this.submittingNote && !this.editingNote) {
      this.kilnService.createFiringNote(this.selected_firing, {...formValue, ...{kiln_id: this.kilnId, external_id: this.selected_firing}})
          .then((data) => {
            this.submittingNote = false;
            this.createForm(data);
            this.showMessage("Successfully created note for firing", false);
          })
          .catch((error) => {
            this.submittingNote = false;
            this.showMessage("Note could not be created at this time. Please try again.", true);
          })
    } else if(!this.submittingNote && this.editingNote) {
      this.kilnService.updateFiringNote(this.selected_firing, {...formValue, ...{kiln_id: this.kilnId, external_id: this.selected_firing}})
          .then((data) => {
            this.submittingNote = false;
            this.createForm(data);
            this.showMessage("Successfully updated note for firing", false);
          })
          .catch((error) => {
            this.submittingNote = false;
            this.showMessage("Note could not be updated at this time. Please try again.", true);
          })
    }
  }

  getHour(details, step) {
    let older_format_firing_information = moment('2021-01-01T00:00')
    if(moment(details.createdAt).isBefore(older_format_firing_information)) {
      return (step.hr === step.mn) ? '00': step.hr;
    } else {
      return step.hr
    }
  }

  hasEnded(details) {
    let last_status = details.updatedAt;
    var timePassed = moment.duration(moment().diff(moment(last_status)));
    var twoHours = moment.duration(2, 'hours');
    return (details && details.ended) ? true : (timePassed > twoHours);
  }

  subTabChange(selectedTabEvent) {
    if(selectedTabEvent && selectedTabEvent.detail) {
      this.subtab = selectedTabEvent.detail.value;
      // if(['results-graph', 'firing-data'].includes(this.subtab)) {
      //   this.getFiringStatuses(this.subtab)
      // }
    } 
  }

  getElapsedTime(details) {
    const start = details?.createdAt ? moment(details.createdAt) : null;
    const end = this.getFiringElapsedEndTime(details);
    if (!start?.isValid() || !end?.isValid()) {
      return '--';
    }

    const duration = moment.duration(end.diff(start));
    const totalMinutes = Math.max(0, Math.floor(duration.asMinutes()));
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;
    const hhmm = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    return days > 0 ? `${days}d ${hhmm}` : hhmm;
  }

  private isCompleteStatus(status: { mode?: string; step?: string }): boolean {
    const mode = String(status?.mode || '').trim();
    const step = String(status?.step || '').trim();
    return mode === 'Complete' || step === 'Complete';
  }

  private getStatusTimestamp(status: { createdAt?: string; date?: string }): moment.Moment | null {
    const ts = moment(status?.createdAt || status?.date);
    return ts.isValid() ? ts : null;
  }

  /** End at the first Complete status; ignore later Complete rows and post-complete time. */
  private getFiringElapsedEndTime(details: {
    createdAt?: string;
    updatedAt?: string;
    statuses?: Array<{ mode?: string; step?: string; createdAt?: string; date?: string }>;
  }): moment.Moment | null {
    const start = details?.createdAt ? moment(details.createdAt) : null;
    if (!start?.isValid()) {
      return null;
    }

    const statuses = Array.isArray(details?.statuses) ? details.statuses : [];
    let firstComplete: moment.Moment | null = null;
    let latestNonComplete: moment.Moment | null = null;

    for (const status of statuses) {
      const ts = this.getStatusTimestamp(status);
      if (!ts || ts.isBefore(start)) {
        continue;
      }

      if (this.isCompleteStatus(status)) {
        if (!firstComplete || ts.isBefore(firstComplete)) {
          firstComplete = ts;
        }
        continue;
      }

      if (!latestNonComplete || ts.isAfter(latestNonComplete)) {
        latestNonComplete = ts;
      }
    }

    if (firstComplete) {
      return firstComplete;
    }

    if (latestNonComplete) {
      return latestNonComplete;
    }

    if (details?.updatedAt) {
      const fallbackEnd = moment(details.updatedAt);
      if (fallbackEnd.isValid() && !fallbackEnd.isBefore(start)) {
        return fallbackEnd;
      }
    }

    return start;
  }

  isHistoryDownloadToggleEnabled(): boolean {
    return this.toggleService.isToggleEnabled('historyDownload');
  }

  private getFiringDownloadId(): string | undefined {
    return this.selected_firing || this.details?.external_id || this.details?.id;
  }

  private hasValidSerial(): boolean {
    const serial = this.serial;
    return !!serial && serial !== 'N/A' && !String(serial).includes('NO_DATA');
  }

  private extractProgramCsvErrorMessage(err: any): string {
    const body = err?.error;
    if (typeof body === 'string') {
      if (body.includes('<!DOCTYPE') || body.includes('Cannot POST')) {
        return this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_REQUEST_FAILED');
      }
      return body.length > 200
        ? this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_REQUEST_FAILED')
        : body;
    }
    if (body?.message && typeof body.message === 'string') {
      if (body.message.includes('<!DOCTYPE') || body.message.includes('Cannot POST')) {
        return this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_REQUEST_FAILED');
      }
      return body.message;
    }
    if (err?.status === 404) {
      return this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_NOT_FOUND');
    }
    return err?.message || this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_REQUEST_FAILED');
  }

  async requestProgramCsvEmail(): Promise<void> {
    const firingId = this.getFiringDownloadId();
    if (!firingId) {
      await this.showMessage(this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_NO_FIRING'), true);
      return;
    }
    if (!this.details?.program?.steps?.length) {
      return;
    }
    if (!this.hasValidSerial()) {
      await this.showMessage(this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_NO_SERIAL'), true);
      return;
    }
    if (this.requestingProgramCsv) {
      return;
    }

    this.requestingProgramCsv = true;
    try {
      const res: any = await this.kilnService.requestFiringProgramCsvEmail(this.serial, firingId);
      if (res?.downloadUrl) {
        window.open(res.downloadUrl, '_blank');
        await this.showMessage(this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_OPEN_DOWNLOAD'), false);
      } else {
        await this.showMessage(this.translate.instant('FIRING_HISTORY.PROGRAM_CSV_EMAIL_SENT'), false);
      }
    } catch (err) {
      await this.showMessage(this.extractProgramCsvErrorMessage(err), true);
    } finally {
      this.requestingProgramCsv = false;
    }
  }
}
